import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between" }
const _hoisted_2 = { class: "d-flex justify-end ga-3" }

import {computed, reactive, ref} from 'vue';
import {useRouter} from 'vue-router';
import { toast } from '@/lib/toast';
import api from '../api';

import ProcedurePage from "../components/ProcedurePage.vue";
import DecisionGrid from "../components/application/DecisionGrid.vue";
import DecisionPositionsGrid from "../components/application/DecisionPositionsGrid.vue";
import AdditionalActions from "@/components/procedure/AdditionalActions.vue";
import AdditionalAction from "@/interfaces/AdditionalAction";
import ProcedureStep from "@/components/ProcedureStep";
import { useProcedureSummingStore } from "@/stores/procedureSummingStore";
import { useLibraryStore } from "@/stores/library";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import CancellationReasonsDecision from '@/components/procedure/CancellationReasonsDecision.vue';

interface ProcedurePosition {
  id: number;
  name: string;
  measureId: number;
  measure: string;
  quantity: number;
  description: string | null;
}

interface Procedure {
  id?: number
  registryNumber?: string
  type?: string
  goodName?: string
  subject?: string
  statusText: string
  startMaxPrice?: string
  applicationDeadline?: null
  summingStart?: null
  shippingCondition?: string
  publishedAt?: null
  organizerName?: string
  isWithoutStartMaxPrice: boolean
  isNds?: boolean
  isClosed: boolean
  withPositions: boolean
  positions: ProcedurePosition[]
  partOfVolume: boolean
  step: {
    name: string
    code: string
  },
  [key: string]: any
}

interface Application {
  isAccepted?: boolean
  isWinner?: boolean
  [key: string]: any
}

interface ApplicationItemDTO {
  id: number
  quantity: number
}
interface ApplicationDTO {
  id: number
  selectedPositions: ApplicationItemDTO[]
}

type SelectedCells = Record<number, Record<number, number>>;


export default /*@__PURE__*/_defineComponent({
  __name: 'ProcedureSumming',
  setup(__props) {

const procedurePage = ref();
const loadingDialogText = ref<string | null>();
const procedure = reactive<Procedure>({
  id: null,
  registryNumber: '',
  type: '',
  goodName: '',
  subject: '',
  statusText: '',
  startMaxPrice: '',
  applicationDeadline: null,
  shippingCondition: '',
  publishedAt: null,
  organizerName: '',
  step: { name: '', code: '' },
  isWithoutStartMaxPrice: false,
  isNds: null,
  isClosed: false,
  withPositions: false,
  positions: [],
  partOfVolume: false,
});
const applications = ref<Application[]>([]);
const additionalActions = ref<AdditionalAction[]>([]);
const types = ref<{
  title: string
  value: string
}[]>([]);
const hasManyRounds = ref(false);
const selectedCells = ref<SelectedCells>({});

const confirmCancellation = ref(null);
const cancellationReasons = ref<{
  id: number,
  explanation: string,
}[]>([]);
const cancellationIsValid = ref<boolean>(false);

const winners = computed<ApplicationDTO[] | null>(() => {
  const apps = applications.value.filter(({ isWinner }) => isWinner);

  if (apps.length === 0) return null;

  return apps.map(app => {
    const selectedPositions: ApplicationItemDTO[] = Object.entries(selectedCells.value)
      .filter(([_, appMap]) => app.id in appMap)
      .map(([positionId, appMap]) => {
        const quantity = appMap[app.id];
        return {
          id: Number(positionId),
          quantity
        };
      });

    return {
      id: app.id,
      isWinner: app.isWinner,
      selectedPositions
    };
  });
});

const procedureSummingStore = useProcedureSummingStore();
const libraryStore = useLibraryStore();

const { REVIEW, SUMMING } = ProcedureStep;
const showDecisionGrid = computed(() =>
  procedureSummingStore.showDecisionGrid ||
  (
    procedure.id &&
    (!procedure.withPositions || procedure.step.code === REVIEW)
  )
);

const showDecisionPositionsGrid = computed(() =>
  procedure.id &&
  procedure.withPositions &&
  procedure.step.code === SUMMING &&
  !procedureSummingStore.showDecisionGrid
);

const load = async procedureId => {
  const resp = await api.get(`/procedures/${procedureId}/summarize`);
  Object.assign(procedure, resp.procedure);
  applications.value = resp.applications;
  additionalActions.value = resp.additionalActions;
  hasManyRounds.value = resp.hasManyRounds;
  types.value = resp.types.map((elem) => ({
    title: elem.name,
    value: elem.code,
  }));

  selectedCells.value = libraryStore.callFunctionByCode('autoSelectBestApplication', {
    procedure: procedure,
    applications: applications
  }).selected ?? {};

  libraryStore.callActionByCode('initProcedureSummingOptions', {
    procedureType: procedure.type,
    procedureStatus: procedure.step.code
  });
};

const router = useRouter();
const goToProcedures = () => router.push({ name: 'procedures.all' });
const goToCompleted = () => router.push({
  name: 'procedures.completed',
  params: { id: procedure.id },
});

const confirm = async procedureId => {
  const approve = await (
    winners.value
      ? (procedurePage.value.confirm.open(
        'Подведение итогов',
        'Вы действительно хотите разместить результаты подведения итогов',
      ))
      : (confirmCancellation.value.open('Завершить процедуру'))
  );
  if (approve) {

    loadingDialogText.value = 'Подводятся итоги';
    try {
      const formData = new FormData()
      const data = winners.value
        ? { winners: winners.value, status: 'yes' }
        : {
          status: 'no',
          reasons: cancellationReasons.value.map((reason) => {
            return {
              id: reason.id,
              explanation: reason.explanation,
            };
          }),
        };
      formData.append('data', JSON.stringify(data));
      await api.post(`/procedures/${procedureId}/summarize`, formData);
      toast.info('Процедура обновлена успешно');
      if (winners.value) {
        goToCompleted();
      } else {
        goToProcedures();
      }
    } catch (e) {
      toast.error(e.message);
    }
    loadingDialogText.value = null;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ProcedurePage, {
      ref_key: "procedurePage",
      ref: procedurePage,
      id: _ctx.$route.params.id,
      procedure: procedure,
      "load-func": load,
      "loading-dialog-text": loadingDialogText.value,
      status: procedure.step,
      types: types.value,
      isClosed: procedure.isClosed,
      "onUpdate:procedureGoodName": _cache[2] || (_cache[2] = ($event: any) => (procedure.goodName = $event)),
      "onUpdate:procedureType": _cache[3] || (_cache[3] = ($event: any) => (procedure.type = $event))
    }, {
      default: _withCtx(() => [
        (showDecisionGrid.value)
          ? (_openBlock(), _createBlock(DecisionGrid, {
              key: 0,
              procedureId: procedure.id,
              "procedure-type": procedure.type,
              status: procedure.step.code,
              applications: applications.value,
              hasManyRounds: hasManyRounds.value,
              isNds: procedure.isNds
            }, null, 8 /* PROPS */, ["procedureId", "procedure-type", "status", "applications", "hasManyRounds", "isNds"]))
          : _createCommentVNode("v-if", true),
        (showDecisionPositionsGrid.value)
          ? (_openBlock(), _createBlock(DecisionPositionsGrid, {
              key: 1,
              applications: applications.value,
              positions: procedure.positions,
              procedure: procedure,
              hasManyRounds: hasManyRounds.value,
              "selected-positions": selectedCells.value,
              "onUpdate:selectedPositions": _cache[0] || (_cache[0] = ($event: any) => ((selectedCells).value = $event))
            }, null, 8 /* PROPS */, ["applications", "positions", "procedure", "hasManyRounds", "selected-positions"]))
          : _createCommentVNode("v-if", true),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(AdditionalActions, {
            procedure: procedure,
            "additional-actions": additionalActions.value
          }, null, 8 /* PROPS */, ["procedure", "additional-actions"]),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_v_btn, {
              flat: "",
              color: "primary",
              density: "comfortable",
              variant: "outlined",
              onClick: goToProcedures
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('Отмена')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_v_btn, {
              flat: "",
              color: "primary",
              density: "comfortable",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (confirm(_ctx.$route.params.id)))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('Завершить процедуру')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            })
          ])
        ])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["id", "procedure", "loading-dialog-text", "status", "types", "isClosed"]),
    _createVNode(ConfirmDialog, {
      ref_key: "confirmCancellation",
      ref: confirmCancellation,
      disabled: !cancellationIsValid.value
    }, {
      content: _withCtx(() => [
        _createVNode(CancellationReasonsDecision, {
          reasons: cancellationReasons.value,
          "onUpdate:reasons": _cache[4] || (_cache[4] = ($event: any) => ((cancellationReasons).value = $event)),
          isValid: cancellationIsValid.value,
          "onUpdate:isValid": _cache[5] || (_cache[5] = ($event: any) => ((cancellationIsValid).value = $event))
        }, null, 8 /* PROPS */, ["reasons", "isValid"])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["disabled"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})